import type { Ordner } from "@kundeninfo/api-client";

/** Ein Knoten des aufgebauten Ordnerbaums — mit der Gestalt der Ordner, aus denen er gebaut ist (etwa mit `boundTo`). */
export type FolderNode<O extends Ordner = Ordner> = { folder: O; children: FolderNode<O>[] };

/**
 * Baut aus der flachen Ordnerliste (jeder mit parentId) den Baum. Sortiert je Ebene nach
 * sortIndex, dann Name — so bleibt die Reihenfolge stabil, auch wenn der Server sie einmal
 * anders liefert. Ordner, deren Elternteil fehlt (dürfte nicht vorkommen), landen an der
 * Wurzel, statt still zu verschwinden. Gemeinsam für den Kundenreiter und die Panels des
 * Abschnitts „Dateien“ am Vorgang.
 */
export function buildFolderTree<O extends Ordner>(folders: readonly O[]): FolderNode<O>[] {
  const nodes = new Map<string, FolderNode<O>>();
  for (const f of folders) nodes.set(f.id, { folder: f, children: [] });
  const roots: FolderNode<O>[] = [];
  for (const f of folders) {
    const node = nodes.get(f.id) as FolderNode<O>;
    const parent = f.parentId !== null ? nodes.get(f.parentId) : undefined;
    if (parent !== undefined) parent.children.push(node);
    else roots.push(node);
  }
  const sort = (list: FolderNode<O>[]): void => {
    list.sort((a, b) => a.folder.sortIndex - b.folder.sortIndex
      || a.folder.name.localeCompare(b.folder.name));
    for (const n of list) sort(n.children);
  };
  sort(roots);
  return roots;
}

/** Die Namen vom obersten Ordner bis zu `id` (leer, wenn es ihn nicht gibt); ein Ring bricht ab. */
export function folderPath(folders: readonly Ordner[], id: string): string[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const path: string[] = [];
  const seen = new Set<string>();
  for (let f = byId.get(id); f !== undefined && !seen.has(f.id); f = f.parentId === null ? undefined : byId.get(f.parentId)) {
    seen.add(f.id);
    path.unshift(f.name);
  }
  return path;
}
